import { createPortal } from 'react-dom';
import CloseIcon from 'assets/images/shopping/closeIcon';
import GenericLoader from 'components/GenericLoader/GenericLoader';
import useLockScroll from 'hooks/useLockScroll';
import { useMemo } from 'react';
import { IShipmentData } from 'types/orders';
import SerialsModal from '../../../orders/Components/SerialsModal/SerialsModal';
import DestinationBlock from './DestinationBlock/DestinationBlock';
import ShipmentProductsBlock from './ShipmentProductsBlock/ShipmentProductsBlock';
import { useShipmentDetailDrawer, NO_INFORMATION_LABEL } from './useShipmentDetailDrawer';
import { ORDER_LISTING_OPERATIONS_ID, TRELLO_COLUMN_CONTENT_CLASS } from '../../trackingOrder.constants';
import './ShipmentDetailDrawer.scss';

interface IShipmentDetailDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  orderId?: number | string;
  shipmentIds?: number[];
}

const ShipmentDetailDrawer = ({
  isOpen,
  onClose,
  orderId,
  shipmentIds: shipmentIdsProp
}: IShipmentDetailDrawerProps) => {
  const shipmentIds = useMemo(
    () => (shipmentIdsProp?.length ? shipmentIdsProp : []),
    [shipmentIdsProp]
  );

  const {
    shipmentDataList,
    isLoading,
    hasAllFailed,
    firstValidShipment,
    destinationDisplay,
    references,
    selectedProductForSerial,
    productsForSerialModal,
    fetchShipmentDetail,
    retryShipmentAtIndex,
    handleOverlayClick,
    handleOpenSerialsModal,
    handleCloseSerialsModal,
    handleSerialsUpdated
  } = useShipmentDetailDrawer({
    isOpen,
    orderId,
    shipmentIds,
    onClose
  });

  const additionalElementsToLock = useMemo(
    () => [ORDER_LISTING_OPERATIONS_ID, TRELLO_COLUMN_CONTENT_CLASS],
    []
  );
  
  useLockScroll(isOpen, additionalElementsToLock);

  const portalContainer = document.getElementById(ORDER_LISTING_OPERATIONS_ID);
  const totalShipments = shipmentDataList.length;
  const showShipmentCounter = totalShipments > 1;
  
  if (!isOpen) return null;

  const drawerContent = (
    <>
      <div className="shipmentDetailDrawerOverlay" onClick={handleOverlayClick} />

      <div className="shipmentDetailDrawer">
        <header className="drawerHeader">
          <h2 className="drawerTitle">Detalle del envío</h2>
          <button className="closeButton" onClick={onClose} aria-label="Cerrar modal">
            <CloseIcon />
          </button>
        </header>

        <div className="drawerContent">
          {isLoading && (
            <div className="loadingState">
              <GenericLoader sizeLoader={60} />
            </div>
          )}

          {hasAllFailed && (
            <div className="errorState">
              <span>Error al cargar el detalle del envío</span>
              <button className="retryButton" type="button" onClick={fetchShipmentDetail}>
                Reintentar
              </button>
            </div>
          )}

          {!isLoading && totalShipments > 0 && (
            <>
              {firstValidShipment && (
                <DestinationBlock
                  shipment={firstValidShipment}
                  destinationDisplay={destinationDisplay}
                  noInformation={NO_INFORMATION_LABEL}
                />
              )}

              {shipmentDataList.map((shipmentData: IShipmentData | null, index: number) => (
                <ShipmentProductsBlock
                  key={shipmentData?.shipmentId ?? `error-${shipmentIds[index]}`}
                  shipmentData={shipmentData}
                  shipmentIndex={index}
                  shipmentId={shipmentIds[index]}
                  showShipmentLabel={showShipmentCounter}
                  onRetry={() => retryShipmentAtIndex(index)}
                  onOpenSerialsModal={handleOpenSerialsModal}
                />
              ))}
            </>
          )}
        </div>
      </div>

      {selectedProductForSerial && (
        <SerialsModal
          singleProduct={selectedProductForSerial}
          products={productsForSerialModal}
          productInfo={{
            name: selectedProductForSerial.name,
            pills: selectedProductForSerial.pills,
            units: productsForSerialModal.length
          }}
          references={references}
          changeShowModal={handleCloseSerialsModal}
          updateWithoutLoaders={handleSerialsUpdated}
        />
      )}
    </>
  );

  if (portalContainer) {
    return createPortal(drawerContent, portalContainer);
  }

  return drawerContent;
};

export default ShipmentDetailDrawer;
